
import React, { useState, useCallback, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/Header';
import Home from './views/Home';
import Shop from './views/Shop';
import Admin from './views/Admin';
import Login from './views/Login';
import CartSidebar from './components/CartSidebar';
import Footer from './components/Footer';
import { CartItem, Product, Order, Variant, PaymentMethod } from './types';
import { supabase } from './supabase';

const App: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [monthlyGoal, setMonthlyGoal] = useState<number>(5000000);
  
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState<boolean>(true);

  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setIsAuthenticated(!!session);
      setIsLoadingAuth(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: productsData } = await supabase.from('products').select('*');
        if (productsData) setProducts(productsData);

        if (isAuthenticated) {
          const { data: ordersData } = await supabase
            .from('orders')
            .select('*')
            .order('date', { ascending: false });
          if (ordersData) setOrders(ordersData);
          
          const { data: goalData } = await supabase
            .from('settings')
            .select('value')
            .eq('key', 'monthly_goal')
            .single();
          if (goalData) setMonthlyGoal(goalData.value);
        }
      } catch (err) {
        console.error("Erreur de récupération :", err);
      }
    };

    if (!isLoadingAuth) fetchData();
  }, [isAuthenticated, isLoadingAuth]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return true;
  };

  const logout = async () => {
    await supabase.auth.signOut();
  };

  const addToCart = useCallback((product: Product, variant?: Variant) => {
    if (product.stock <= 0) return;
    setCartItems(prev => {
      const existingIndex = prev.findIndex(item => 
        item.id === product.id && (!variant || item.selectedVariant?.id === variant.id)
      );
      if (existingIndex > -1) {
        const newItems = [...prev];
        if (newItems[existingIndex].quantity < product.stock) {
          newItems[existingIndex] = { ...newItems[existingIndex], quantity: newItems[existingIndex].quantity + 1 };
        }
        return newItems;
      }
      return [...prev, { ...product, quantity: 1, selectedVariant: variant }];
    });
    setIsCartOpen(true);
  }, []);

  const removeFromCart = useCallback((id: string, variantId?: string) => {
    setCartItems(prev => prev.filter(item => !(item.id === id && (!variantId || item.selectedVariant?.id === variantId))));
  }, []);

  const updateQuantity = useCallback((id: string, variantId: string | undefined, delta: number) => {
    setCartItems(prev => prev.map(item => {
      if (item.id === id && (!variantId || item.selectedVariant?.id === variantId)) {
        const newQty = Math.max(1, item.quantity + delta);
        if (newQty <= item.stock) {
          return { ...item, quantity: newQty };
        }
      }
      return item;
    }));
  }, []);

  const handleWhatsAppOrder = useCallback((customerName: string, customerPhone: string, paymentMethod: PaymentMethod) => {
    // 1. Préparation immédiate des données
    const total = cartItems.reduce((acc, item) => acc + (item.price * item.quantity), 0);
    const phoneNumber = "221788984561";

    const itemList = cartItems.map(i => {
      const variantStr = i.selectedVariant ? ` [Couleur: ${i.selectedVariant.name}]` : '';
      return `- ${i.name}${variantStr} (x${i.quantity}) : ${i.price * i.quantity} FCFA`;
    }).join('\n');
    
    const messageText = `Bonjour Khamal Bags ! Je suis ${customerName} (${customerPhone}).\nJe souhaite passer cette commande :\n\n${itemList}\n\nTotal : ${total} FCFA\nMode de paiement : ${paymentMethod}\n\nMerci de me confirmer la disponibilité.`;
    
    // 2. REDIRECTION IMMÉDIATE (CRITIQUE : Doit se faire avant tout await pour éviter le blocage navigateur)
    window.open(`https://wa.me/${phoneNumber}?text=${encodeURIComponent(messageText)}`, '_blank');

    // 3. Traitement Background (Base de données)
    // On ne "await" pas ces promesses pour ne pas bloquer l'UI, on les laisse s'exécuter
    const processOrderInBackground = async () => {
      // A. Mise à jour des stocks
      const updatedProducts = [...products];
      try {
        for (const item of cartItems) {
          const pIdx = updatedProducts.findIndex(p => p.id === item.id);
          if (pIdx > -1) {
            const p = updatedProducts[pIdx];
            const newStock = Math.max(0, (Number(p.stock) || 0) - item.quantity);
            
            await supabase.from('products')
              .update({ stock: newStock, isOutOfStock: newStock <= 0 })
              .eq('id', p.id);
            
            updatedProducts[pIdx] = { ...p, stock: newStock, isOutOfStock: newStock <= 0 };
          }
        }
        setProducts(updatedProducts); // Mise à jour optimiste de l'UI locale
      } catch (err) {
        console.error("Erreur background stock :", err);
      }

      // B. Enregistrement commande
      try {
        const { data, error } = await supabase
          .from('orders')
          .insert([{
            customerName: customerName,
            customerPhone: customerPhone,
            paymentMethod: paymentMethod,
            total: total,
            items: cartItems,
            status: 'en cours'
          }])
          .select();

        if (error) console.error("Erreur sauvegarde commande :", error.message);
        else if (data) {
          setOrders(prev => [data[0], ...prev]);
        }
      } catch (err) {
        console.error("Erreur background commande :", err);
      }
    };

    // Lancement du processus en arrière-plan
    processOrderInBackground();

    // 4. Nettoyage UI immédiat
    setCartItems([]);
    setIsCartOpen(false);
  }, [cartItems, products]);

  if (isLoadingAuth) {
    return <div className="min-h-screen bg-brand-offwhite flex items-center justify-center font-serif italic text-brand-gold">Initialisation Khamal Bags...</div>;
  }

  return (
    <Router>
      <div className="min-h-screen font-sans selection:bg-brand-gold selection:text-white">
        <Header cartCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)} onCartClick={() => setIsCartOpen(true)} />
        <main>
          <Routes>
            <Route path="/" element={<Home addToCart={addToCart} products={products} />} />
            <Route path="/shop" element={<Shop addToCart={addToCart} products={products} />} />
            <Route path="/login" element={<Login onLogin={login} isAuth={isAuthenticated} />} />
            <Route path="/admin" element={isAuthenticated ? <Admin products={products} orders={orders} setProducts={setProducts} setOrders={setOrders} onLogout={logout} monthlyGoal={monthlyGoal} setMonthlyGoal={setMonthlyGoal} /> : <Navigate to="/login" />} />
          </Routes>
        </main>
        <Footer />
        <CartSidebar isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} items={cartItems} onRemove={removeFromCart} onUpdateQty={updateQuantity} onCheckout={handleWhatsAppOrder} />
      </div>
    </Router>
  );
};

export default App;
